"use client";
import {
  ButtonComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
} from "@/components";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import {
  useConfirmRpdModeMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useRpdmodesQuery,
  useVkycDetailsQuery,
} from "@/lib/redux";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { ref } from "yup";

const VerificationMode = ({ product }) => {
  const router = useRouter();
  const [value, setValue] = useState("");
  const [scheduledModal, setScheduledModal] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const { setActions } = useFooter();
  const {
    data: applicationData,
    isLoading: applicationLoading,
    error: errorGetApplication,
    refetch: refetchApplication,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });

  const { data: customerData } = useGetCustomerQuery();

  const [
    confirmRpdMode,
    { data, isLoading: confirmingMode, isSuccess: rpdModeSuccess },
  ] = useConfirmRpdModeMutation();
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const {
    data: vkycDetailsData,
    error: errorVkycDetailsData,
    isLoading: vkycDetailsLoading,
    refetch: refetchVkycDetails,
  } = useVkycDetailsQuery(
    { appId: productApplication?.appId },
    { refetchOnMountOrArgChange: true, skip: !productApplication?.appId },
  );

  const { data: rpdModes, isLoading: rpdModesLoading } = useRpdmodesQuery(
    productApplication?.appId,
    {
      skip: !productApplication?.appId,
    },
  );

  const handleModeSelection = async (value, isDigiKycCompleted) => {
    if (!value) return;
    if (vkycDetailsData?.data?.vkycApplicationStatus === "needs_review") {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    }

    if (value?.toLowerCase() === "vkyc") {
      sessionStorage.setItem("vkycFrom", "verificationMode");
      router.push(STAGE_BASED_ROUTING(STAGE.VKYC));
      refetchVkycDetails();
      vkycHandling();
      return;
    } else if (value?.toLowerCase() === "physical_verification") {
      sendEventAnalytics({
        eventName: WEB_EVENTS.CPV_SELECTED,
        ...productApplication,
        ...customerData?.data,
      });
      confirmRpdMode({ appId: productApplication?.appId, mode: value });
    } else {
      sendEventAnalytics({
        eventName: WEB_EVENTS.RPD_SELECTED,
        ...productApplication,
        ...customerData?.data,
      });
      if (isDigiKycCompleted) {
        router.push(STAGE_BASED_ROUTING(STAGE.REVERSE_PENNY_DROP));
        return;
      } else {
        try {
          const response = await confirmRpdMode({
            appId: productApplication?.appId,
            mode: value,
          }).unwrap();
          refetchApplication();
        } catch (error) {
        }
      }
    }
  };

  const vkycHandling = () => {
    if (vkycDetailsData?.data?.vkycHoliday) {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    }
    if (vkycDetailsData?.data?.vkycApplicationStatus === "needs_review") {
      setModalDescription(vkycDetailsData?.data?.vkycMessage);
      setScheduledModal(true);
      return;
    } else {
      router.push(STAGE_BASED_ROUTING(STAGE.VKYC));
    }
  };
  const isLoading = applicationLoading || confirmingMode || rpdModesLoading;
  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: true,
      onConfirm: () => {
        const rpdModeList = rpdModes?.data;
        const obj = rpdModeList.filter(
          (modes) => modes?.mode?.toLowerCase() === value?.toLowerCase(),
        );
        const isDigiKycCompleted = obj?.[0]?.isDigiKycCompleted;
        handleModeSelection(value, Boolean(isDigiKycCompleted));
      },
      confirmBtnLabel: "Proceed",
    });
  }, [productApplication?.appId, value, rpdModes]);

  useEffect(() => {
    refetchApplication();
  }, [rpdModeSuccess]);

  useEffect(() => {}, [productApplication?.stage]);

  useEffect(() => {
    if (
      productApplication?.stage &&
      productApplication?.stage !== STAGE?.RPD_MODE
    ) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage]);

  return (
    <Stack rowGap={2} mb={2}>
      <LoaderComponent open={isLoading} />
      <PaperComponent
        elevation={0}
        paperStyle={{ backgroundColor: "rgba(240, 240, 240, 0.6)" }}
      >
        <LoaderComponent open={isLoading} />
        <Stack spacing={2}>
          <Stack alignItems={"center"} spacing={2}>
            <Image src={IMAGES.vkycFail} alt="VKYC Failed" />
            <Typography
              align="center"
              fontWeight={700}
              fontSize={{ xs: 18, sm: 24 }}
              color={WEB_COLORS.primary}
            >
              We couldn’t complete your Verification via VKYC
            </Typography>
          </Stack>
          <Typography
            fontWeight={400}
            color={WEB_COLORS.primary}
            fontSize={{ xs: 16, sm: 20 }}
          >
            Choose an Alternate Verification Mode
          </Typography>
        </Stack>
        <FormControl fullWidth>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={value}
          >
            {rpdModes?.data?.map((modes) => (
              <PaperComponent
                key={modes?.id}
                elevation={0}
                paperStyle={{
                  border: "1px solid #CCD6D4",
                  backgroundColor:
                    value === modes?.mode ? "#E5F1F1" : "transparent",
                  cursor: "pointer",
                }}
                onClick={() => setValue(modes?.mode)}
              >
                <FormControlLabel
                  sx={{
                    width: "100%",
                    color: WEB_COLORS.primary,
                    pointerEvents: "none",
                  }}
                  value={modes?.mode}
                  control={<Radio />}
                  label={
                    <Typography sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}>
                      {modes?.label}
                    </Typography>
                  }
                />
                {value === modes?.mode && (
                  <PaperComponent elevation={0}>
                    {modes.addressType && (
                      <Typography fontSize={14}>
                        {modes?.addressType}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: { xs: 14, sm: 16 } }}>
                      {modes?.description}
                    </Typography>
                  </PaperComponent>
                )}
              </PaperComponent>
            ))}
          </RadioGroup>
        </FormControl>
      </PaperComponent>
      <ModalComponent
        closeButtonRequired={false}
        title={
          vkycDetailsData?.data?.vkycAgentStatus === "SCHEDULED" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "needs_review" &&
          vkycDetailsData?.data?.vkycApplicationStatus !==
            "manually_declined" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "auto_declined" &&
          vkycDetailsData?.data?.vkycApplicationStatus !== "manually_approved"
            ? "Scheduled Slot Timing"
            : ""
        }
        open={scheduledModal}
      >
        <Stack
          alignItems={"center"}
          justifyContent={"center"}
          spacing={2}
          p={1}
        >
          <Image src={IMAGES.document} alt="Technical Issues" />
          <Typography align="center">{modalDescription}</Typography>
          <ButtonComponent
            onClick={async () => {
              await refetchVkycDetails();
              await refetchApplication();
              setScheduledModal(false);
              if (
                vkycDetailsData?.data?.vkycApplicationStatus ===
                "manually_approved"
              ) {
                if (
                  productApplication?.stage !== undefined &&
                  productApplication?.stage !== null &&
                  productApplication?.stage !== STAGE?.VKYC &&
                  productApplication?.stage !== STAGE?.VKYC_SCHEDULED
                )
                  STAGE_BASED_ROUTING(productApplication?.stage);
              } else if (
                vkycDetailsData?.data?.vkycApplicationStatus ===
                  "needs_review" ||
                vkycDetailsData?.data?.vkycApplicationStatus ===
                  "manually_declined" ||
                vkycDetailsData?.data?.vkycApplicationStatus === "auto_declined"
              ) {
                router.push("/");
              }
            }}
          >
            Ok
          </ButtonComponent>
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default VerificationMode;
